// Layout do Campus Agzos: em que prédio, andar e posição cada sala fica.
//
// Regras:
//   - um prédio por grupo de projetos irmãos (buildingKeyFor: agzos-office e agzos-code
//     ficam no prédio "agzos"); projetos sem irmão vão juntos para o "Coworking";
//   - a SEDE (o maior grupo, de preferência 'agzos') tem todas as áreas comuns; os outros
//     prédios têm só copa e lounge no térreo;
//   - cada andar é uma fileira de salas com as portas para baixo, um vão de parede e um
//     corredor de 3 tiles embaixo;
//   - o térreo tem as áreas comuns; prédio pequeno (≤ 4 projetos) tem um andar só, com
//     tudo; prédio grande sobe: 3 projetos no térreo e 4 por andar, cada andar com uma
//     área comum própria, e o último com o terraço;
//   - abaixo dos prédios: calçada, rua de 4 faixas de tile e outra calçada.
//
// Tudo em tiles, coordenadas absolutas do mundo. Puro: os testes rodam em Node.

import type { Agent, Room } from '../../core/types.js';
import { buildingByRules, type CampusSettings, type CommonArea } from '../../core/settings.js';
import { buildingKeyFor, mapSize, type Rect, type RoomMap } from '../../core/world.js';
import { BANHEIROS } from '../maps/banheiros.js';
import { COPA } from '../maps/copa.js';
import { LOUNGE } from '../maps/lounge.js';
import { projectRoom } from '../maps/project.js';
import { RECEPCAO } from '../maps/recepcao.js';
import { REUNIAO } from '../maps/reuniao.js';
import { SERVIDORES } from '../maps/servidores.js';
import { TERRACO } from '../maps/terraco.js';

/** Grama antes do primeiro prédio e entre prédios. */
const MARGIN = 4;
const BUILDING_GAP = 5;
/** Espaço acima das salas para a parede norte (24 px) e o telhado. */
const TOP = 3;
/** Vão (parede) entre salas e entre a fileira de salas e o corredor. */
const GAP = 1;
const CORRIDOR_H = 3;
/** Projetos que cabem num prédio de andar único. */
const SINGLE_FLOOR_MAX = 4;
const GROUND_PROJECTS = 3;
const FLOOR_PROJECTS = 4;
/** Mesas mínimas numa sala de projeto (sobra mesa para subagente chegar). */
const MIN_DESKS = 3;
/** Prédio dos projetos sem irmão. */
const COWORKING = 'coworking';

export interface PlacedMap {
  /** Id único no mundo (ex.: 'agzos/0/copa', 'room:agzos-office'). */
  id: string;
  map: RoomMap;
  x: number;
  y: number;
  w: number;
  h: number;
  label: string;
  /** Sala de projeto: o Room.id do escritório. */
  roomId?: string;
}

export interface CampusFloor {
  level: number;
  name: string;
  rooms: PlacedMap[];
  corridor: PlacedMap;
  /** Tiles do vão de parede que viram passagem (abaixo de cada porta). */
  doorways: Array<{ x: number; y: number }>;
  bounds: Rect;
}

export interface CampusBuilding extends Rect {
  id: string;
  name: string;
  floors: CampusFloor[];
}

export interface AgentHome {
  building: string;
  level: number;
  /** PlacedMap.id da sala de projeto. */
  room: string;
  /** Índice da mesa (ordem do agente na sala). */
  desk: number;
}

export interface CampusLayout {
  w: number;
  h: number;
  buildings: CampusBuilding[];
  /** Faixas do exterior (y em tiles). */
  sidewalkY: number;
  streetY: number;
  streetH: number;
  homes: Map<string, AgentHome>;
}

function corridorMap(id: string, w: number): RoomMap {
  return {
    id,
    kind: 'hall',
    name: 'Corredor',
    floor: Array.from({ length: CORRIDOR_H }, () => 'c'.repeat(w)),
    legend: { c: 'porcelanato' },
    items: [],
    doors: [],
    openTop: true,
  };
}

/** Monta uma fileira de salas + corredor a partir de (x, y). */
function buildFloor(buildingId: string, level: number, maps: Array<{ map: RoomMap; label: string; roomId?: string; id: string }>, x0: number, y0: number): CampusFloor {
  const sizes = maps.map((m) => mapSize(m.map));
  const rowH = Math.max(1, ...sizes.map((s) => s.h));
  const rooms: PlacedMap[] = [];
  const doorways: CampusFloor['doorways'] = [];
  let x = x0 + GAP;
  maps.forEach((m, i) => {
    const size = sizes[i]!;
    const y = y0 + rowH - size.h; // alinhadas por baixo: todas as portas encostam no corredor
    rooms.push({ id: m.id, map: m.map, x, y, w: size.w, h: size.h, label: m.label, ...(m.roomId ? { roomId: m.roomId } : {}) });
    for (const door of m.map.doors) doorways.push({ x: x + door.x, y: y0 + rowH });
    x += size.w + GAP;
  });
  const width = x - x0;
  const corridorY = y0 + rowH + GAP;
  const corridor: PlacedMap = { id: `${buildingId}/${level}/corredor`, map: corridorMap(`${buildingId}/${level}/corredor`, width - 2 * GAP), x: x0 + GAP, y: corridorY, w: width - 2 * GAP, h: CORRIDOR_H, label: 'Corredor' };
  return {
    level,
    name: level === 0 ? 'Térreo' : `${level}º andar`,
    rooms,
    corridor,
    doorways,
    bounds: { x: x0, y: y0 - TOP, w: width, h: TOP + rowH + GAP + CORRIDOR_H + GAP },
  };
}

function common(buildingId: string, level: number, map: RoomMap) {
  return { map, label: map.name, id: `${buildingId}/${level}/${map.id}` };
}

interface ProjectSlot {
  room: Room;
  desks: number;
  label: string;
  /** Andar preferido (ajuste do CMS). */
  floor?: number;
}

/**
 * Distribui os projetos em andares: quem tem andar preferido vai para ele; o resto enche os
 * andares na ordem, respeitando a capacidade (térreo `cap0`, demais `capN`).
 */
export function distribute<T extends { floor?: number }>(projects: readonly T[], cap0: number, capN: number): T[][] {
  const levels: T[][] = [];
  const at = (n: number) => (levels[n] ??= []);
  for (const p of projects) if (p.floor !== undefined) at(p.floor).push(p);
  let level = 0;
  for (const p of projects) {
    if (p.floor !== undefined) continue;
    while (at(level).length >= (level === 0 ? cap0 : capN)) level++;
    at(level).push(p);
  }
  for (let i = 0; i < levels.length; i++) levels[i] ??= [];
  return levels.length ? levels : [[]];
}

/** Distribui os projetos de um prédio em andares. */
function floorsFor(buildingId: string, projects: ProjectSlot[], x0: number, y0: number, hq: boolean, commons: CampusSettings['commons'] | undefined): CampusFloor[] {
  const proj = (p: ProjectSlot) => ({
    map: projectRoom(p.room.id, p.label, Math.max(MIN_DESKS, p.desks)),
    label: p.label,
    roomId: p.room.id,
    id: p.room.id,
  });
  const on = (m: RoomMap) => commons?.[m.id as CommonArea] !== false;
  if (!hq) {
    const levels = distribute(projects, GROUND_PROJECTS, FLOOR_PROJECTS);
    return levels.map((list, level) => buildFloor(buildingId, level, [...(level === 0 ? [COPA, LOUNGE].map((m) => common(buildingId, 0, m)) : []), ...list.map(proj)], x0, y0));
  }
  const ground = [RECEPCAO, COPA, LOUNGE, BANHEIROS, SERVIDORES].filter(on);
  const wantsUpper = projects.some((p) => (p.floor ?? 0) > 0);
  if (projects.length <= SINGLE_FLOOR_MAX && !wantsUpper) {
    return [buildFloor(buildingId, 0, [...ground.map((m) => common(buildingId, 0, m)), ...[REUNIAO].filter(on).map((m) => common(buildingId, 0, m)), ...projects.map(proj)], x0, y0)];
  }
  const levels = distribute(projects, GROUND_PROJECTS, FLOOR_PROJECTS);
  const upper = levels.length - 1;
  return levels.map((list, level) => {
    if (level === 0) return buildFloor(buildingId, 0, [...ground.map((m) => common(buildingId, 0, m)), ...list.map(proj)], x0, y0);
    const extras = [level % 2 === 1 ? REUNIAO : COPA, LOUNGE];
    if (level === upper) extras.push(TERRACO);
    return buildFloor(buildingId, level, [...extras.filter(on).map((m) => common(buildingId, level, m)), ...list.map(proj)], x0, y0);
  });
}

function title(key: string): string {
  return key.charAt(0).toUpperCase() + key.slice(1);
}

/** O campus inteiro para as salas e agentes de agora. */
/** O campus inteiro para as salas e agentes de agora, com os ajustes do CMS (se houver). */
export function layoutCampus(rooms: readonly Room[], agents: readonly Agent[], campus?: CampusSettings): CampusLayout {
  const roomCfg = (r: Room) => campus?.rooms[r.project];
  const visible = rooms.filter((r) => !roomCfg(r)?.hidden);
  // prédio: ajuste da sala > regras > prefixo do projeto; projeto sem irmão (e sem ajuste) vai para o Coworking
  const byKey = new Map<string, Room[]>();
  const pinned = new Set<string>();
  for (const room of visible) {
    const explicit = roomCfg(room)?.building ?? buildingByRules(room.project, campus?.rules ?? []);
    const key = explicit ?? buildingKeyFor(room.cwd || room.project);
    if (explicit) pinned.add(room.id);
    byKey.set(key, [...(byKey.get(key) ?? []), room]);
  }
  const groups = new Map<string, Room[]>();
  for (const [key, list] of byKey) {
    const alone = list.length === 1 && key !== 'agzos' && key !== campus?.hq && !pinned.has(list[0]!.id);
    const target = alone ? COWORKING : key;
    groups.set(target, [...(groups.get(target) ?? []), ...list]);
  }
  if (!groups.size) groups.set(campus?.hq || 'agzos', []);
  const hqKey = campus?.hq && groups.has(campus.hq) ? campus.hq : groups.has('agzos') ? 'agzos' : [...groups.entries()].sort((a, b) => b[1].length - a[1].length)[0]![0];
  const orderOf = (k: string) => campus?.buildings[k]?.order;

  const buildings: CampusBuilding[] = [];
  const homes = new Map<string, AgentHome>();
  let x = MARGIN;
  const y0 = MARGIN + TOP;
  const order = [...groups.keys()].sort((a, b) => {
    const oa = orderOf(a);
    const ob = orderOf(b);
    if (oa !== undefined || ob !== undefined) return (oa ?? 1e9) - (ob ?? 1e9);
    return a === hqKey ? -1 : b === hqKey ? 1 : a === COWORKING ? 1 : b === COWORKING ? -1 : a.localeCompare(b);
  });
  for (const key of order) {
    const list = groups.get(key) ?? [];
    const projects: ProjectSlot[] = [...list]
      .sort((a, b) => a.project.localeCompare(b.project))
      .map((room) => {
        const cfg = roomCfg(room);
        return { room, desks: Math.max(room.agentIds.length, cfg?.desks ?? 0), label: cfg?.name ?? room.project, ...(cfg?.floor !== undefined ? { floor: cfg.floor } : {}) };
      });
    const hq = key === hqKey;
    const floors = floorsFor(key, projects, x, y0, hq, campus?.commons);
    const w = Math.max(...floors.map((f) => f.bounds.w));
    const h = Math.max(...floors.map((f) => f.bounds.h));
    const name = campus?.buildings[key]?.name ?? (hq ? `Sede ${title(key)}` : key === COWORKING ? 'Coworking' : `Prédio ${title(key)}`);
    buildings.push({ id: key, name, x, y: y0 - TOP, w, h, floors });
    for (const floor of floors) {
      for (const placed of floor.rooms) {
        if (!placed.roomId) continue;
        const room = list.find((r) => r.id === placed.roomId);
        room?.agentIds.forEach((agentId, desk) => homes.set(agentId, { building: key, level: floor.level, room: placed.id, desk }));
      }
    }
    x += w + BUILDING_GAP;
  }
  void agents;
  const bottom = Math.max(...buildings.map((b) => b.y + b.h));
  const sidewalkY = bottom + 1;
  const streetY = sidewalkY + 2;
  const streetH = 4;
  return { w: x - BUILDING_GAP + MARGIN, h: streetY + streetH + 2 + MARGIN, buildings, sidewalkY, streetY, streetH, homes };
}
